import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    KeyboardArrowRight,
    LocalFireDepartmentRounded,
    SportsEsportsRounded,
    CalendarMonthRounded,
    MapRounded,
    ShowChartRounded,
    TimelineRounded,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import OverviewRecentMatchesTable from "./OverviewRecentMatchesTable";
import OverviewEloProgressionChart, {
    formatProgressionSubtitle,
} from "./OverviewEloProgressionChart";
import OverviewMapPerformance from "./OverviewMapPerformance";
import OverviewActivityCalendar from "./OverviewActivityCalendar";
import OverviewRatingTrendChart from "./OverviewRatingTrendChart";
import { buildEloProgressionSeries, buildProfileMatches, getHighestWinStreak } from "./profileRecentMatches";
import { EmptyDataState } from "../common/EmptyDataState";
import ProfileStatsBar from "./ProfileStatsBar";

const MotionBox = motion(Box);

const OVERVIEW_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 24px 60px rgba(0,0,0,0.42)
    `,
    gap: "1rem",
    padding: "1.5rem",
    minWidth: 0,
};

const CARD_TOP_GLOW_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "42%",
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(246,185,76,0.95),
            transparent
        )
    `,
    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
    pointerEvents: "none",
};

const CARD_HEADER_SX = {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
    paddingBottom: "1rem",
    marginBottom: "0.4rem",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
};

const CARD_TITLE_SX = {
    fontSize: "0.72rem",
    fontWeight: 800,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: colors.text.primary,
    lineHeight: 1,
};

const CARD_SUBTITLE_SX = {
    fontSize: "0.68rem",
    fontWeight: 500,
    letterSpacing: "0.04em",
    color: "rgba(255,255,255,0.42)",
};

function WinLossRecordLabel({ wins = 0, losses = 0 }) {
    const renderSide = (letter, count, color) => (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
            <Box
                sx={{
                    minWidth: "1.15rem",
                    height: "1.15rem",
                    borderRadius: "4px",
                    bgcolor: color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.62rem",
                        fontWeight: 800,
                        color: colors.text.primary,
                        lineHeight: 1,
                    }}
                >
                    {letter}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {count}
            </Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.45rem",
                flexShrink: 0,
            }}
        >
            {renderSide("W", wins, colors.state.success.main)}
            <Typography
                sx={{
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: colors.text.muted,
                    lineHeight: 1,
                }}
            >
                /
            </Typography>
            {renderSide("L", losses, colors.state.danger.main)}
        </Box>
    );
}

WinLossRecordLabel.propTypes = {
    wins: PropTypes.number,
    losses: PropTypes.number,
};

export default function ProfileOverviewTab({
    last25MatchData = {},
    mapsStats = {},
    activityMatches = [],
    recentStats = {},
    isLoading = false,
    onViewAllMatches,
    statsBarItems = [],
    statsBarLoading = false,
}) {
    const progressionMatchCount = useMemo(
        () => buildEloProgressionSeries(last25MatchData, 30).matchCount,
        [last25MatchData]
    );

    const highestWinStreak = useMemo(
        () => getHighestWinStreak(last25MatchData, 30),
        [last25MatchData]
    );

    const hasMatches = useMemo(
        () => buildProfileMatches(last25MatchData).length > 0,
        [last25MatchData]
    );

    if (!isLoading && !hasMatches) {
        return (
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    alignSelf: "stretch",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                }}
            >
                <ProfileStatsBar stats={statsBarItems} isLoading={statsBarLoading} />
                <Box
                    sx={{
                        display: { xs: "flex", lg: "grid" },
                        gridTemplateColumns: {
                            lg: "minmax(0, 1.65fr) minmax(0, 1fr)",
                        },
                        gridTemplateRows: { lg: "auto auto" },
                        flexDirection: { xs: "column", lg: "unset" },
                        width: "100%",
                        gap: "1rem",
                        alignItems: "stretch",
                    }}
                >
                    <EmptyDataState
                        sectionTitle="Recent Matches"
                        sectionSubtitle="Last competitive games"
                        title="No match history available"
                        icon={SportsEsportsRounded}
                        minHeight={{ xs: "14rem", lg: "100%" }}
                        sx={{ gridRow: { lg: "1 / 3" }, gridColumn: { lg: "1" } }}
                    >
                        Matches needed to unlock recent results
                    </EmptyDataState>
                    <EmptyDataState
                        sectionTitle="Elo Progression"
                        sectionSubtitle="Last 30 matches"
                        title="No stats available"
                        icon={ShowChartRounded}
                        minHeight="14rem"
                    >
                        Matches needed to unlock Elo progression
                    </EmptyDataState>
                    <EmptyDataState
                        sectionTitle="Activity"
                        sectionSubtitle="Play calendar"
                        title="No activity yet"
                        icon={CalendarMonthRounded}
                        minHeight="12rem"
                    >
                        Play matches to fill your activity calendar
                    </EmptyDataState>
                </Box>
                <Box
                    sx={{
                        display: { xs: "flex", lg: "grid" },
                        gridTemplateColumns: {
                            lg: "minmax(0, 1fr) minmax(0, 1fr)",
                        },
                        flexDirection: { xs: "column", lg: "unset" },
                        width: "100%",
                        gap: "1rem",
                        alignItems: "stretch",
                    }}
                >
                    <EmptyDataState
                        sectionTitle="Rating 3.0"
                        sectionSubtitle="Performance trend"
                        title="No rating data"
                        icon={TimelineRounded}
                        minHeight="14rem"
                    >
                        Matches needed to unlock Rating 3.0 trends
                    </EmptyDataState>
                    <EmptyDataState
                        sectionTitle="Map Performance"
                        sectionSubtitle="Best maps"
                        title="No map data"
                        icon={MapRounded}
                        minHeight="14rem"
                    >
                        Matches needed to unlock map performance
                    </EmptyDataState>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: "100%",
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
            }}
        >
            <ProfileStatsBar stats={statsBarItems} isLoading={statsBarLoading} />
            <Box
                sx={{
                    display: { xs: "flex", lg: "grid" },
                    gridTemplateColumns: {
                        lg: "minmax(0, 1.65fr) minmax(0, 1fr)",
                    },
                    gridTemplateRows: { lg: "auto auto" },
                    flexDirection: { xs: "column", lg: "unset" },
                    width: "100%",
                    gap: "1rem",
                    alignItems: "stretch",
                }}
            >
                <Box
                    sx={{
                        ...OVERVIEW_CARD_SX,
                        gridRow: { lg: "1 / 3" },
                        gridColumn: { lg: "1" },
                        minHeight: 0,
                        // Avoid clipping hovered match rows (lift + shadow)
                        overflow: { xs: "visible", lg: "hidden" },
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.03,
                            backgroundImage: `
                            radial-gradient(
                                rgba(255,255,255,0.8) 0.4px,
                                transparent 0.4px
                            )
                        `,
                            backgroundSize: "6px 6px",
                            pointerEvents: "none",
                        }}
                    />
                    <MotionBox
                        animate={{
                            opacity: [0.16, 0.28, 0.16],
                            scale: [1, 1.03, 1],
                        }}
                        transition={{
                            repeat: Infinity,
                            duration: 6,
                            ease: "easeInOut",
                        }}
                        sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: 0,
                            transform: "translateX(-50%)",
                            width: "100%",
                            height: "55%",
                            background: `
                                radial-gradient(
                                    ellipse at bottom,
                                    rgba(246,185,76,0.14) 0%,
                                    rgba(246,185,76,0.06) 28%,
                                    transparent 72%
                                )
                            `,
                            filter: "blur(60px)",
                            pointerEvents: "none",
                        }}
                    />
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingBottom: "1rem",
                            marginBottom: "0.4rem",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.text.primary,
                                    lineHeight: 1,
                                }}
                            >
                                Recent Matches
                            </Typography>

                            <Typography
                                sx={{
                                    mt: "0.35rem",
                                    fontSize: "0.68rem",
                                    fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    color: "rgba(255,255,255,0.42)",
                                }}
                            >
                                Last competitive activity
                            </Typography>
                        </Box>
                        {onViewAllMatches ? (
                            <MotionBox
                                whileHover={{
                                    y: -2,
                                }}
                                whileTap={{
                                    scale: 0.985,
                                }}
                                component="button"
                                type="button"
                                onClick={onViewAllMatches}
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: "0.3rem", md: "0.55rem" },
                                    flexShrink: 0,
                                    height: { xs: "2.1rem", md: "2.6rem" },
                                    padding: { xs: "0 0.7rem", md: "0 1rem" },
                                    borderRadius: { xs: "10px", md: "14px" },
                                    border: "1px solid rgba(255,255,255,0.06)",
                                    background: `
                                        linear-gradient(
                                            180deg,
                                            rgba(255,255,255,0.04),
                                            rgba(255,255,255,0.015)
                                        )
                                    `,
                                    color: colors.text.primary,
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    transition: `
                                        all 240ms cubic-bezier(0.22,1,0.36,1)
                                    `,
                                    boxShadow: `
                                        inset 0 1px 0 rgba(255,255,255,0.05),
                                        0 10px 24px rgba(0,0,0,0.24)
                                    `,
                                    "&:hover": {
                                        borderColor: alpha(colors.primary.main, 0.18),
                                        boxShadow: `
                                    inset 0 1px 0 rgba(255,255,255,0.05),
                                    0 0 24px rgba(246,185,76,0.08),
                                    0 12px 28px rgba(0,0,0,0.3)
                                `,
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: { xs: "0.6rem", md: "0.68rem" },
                                        fontWeight: 700,
                                        letterSpacing: {
                                            xs: "0.08em",
                                            md: "0.14em",
                                        },
                                        textTransform: "uppercase",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    View All
                                </Typography>

                                <KeyboardArrowRight
                                    sx={{
                                        fontSize: { xs: "0.85rem", md: "1rem" },
                                        color: colors.text.primary,
                                    }}
                                />
                            </MotionBox>
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            flex: 1,
                            minHeight: 0,
                            overflowY: { xs: "visible", lg: "auto" },
                            overflowX: "hidden",
                            // Room for hover lift/shadow so rows don't clip
                            pb: { xs: "0.35rem", lg: "0.15rem" },
                        }}
                    >
                        <OverviewRecentMatchesTable
                            last25MatchData={last25MatchData}
                            isLoading={isLoading}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        ...OVERVIEW_CARD_SX,
                        gridColumn: { lg: 2 },
                        gridRow: { lg: 1 },
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box
                        sx={{
                            ...CARD_HEADER_SX,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.35rem",
                                minWidth: 0,
                            }}
                        >
                            <Typography sx={CARD_TITLE_SX}>Elo Progression</Typography>
                            <Typography sx={CARD_SUBTITLE_SX}>
                                {formatProgressionSubtitle(progressionMatchCount)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                position: "relative",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.45rem",
                                flexShrink: 0,
                                px: "0.7rem",
                                py: "0.42rem",
                                borderRadius: "999px",
                                background: `
                                    linear-gradient(
                                        180deg,
                                        ${alpha(colors.primary.main, 0.14)} 0%,
                                        ${alpha(colors.primary.main, 0.08)} 100%
                                    )
                                `,
                                border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
                                boxShadow: `
                                    inset 0 1px 0 rgba(255,255,255,0.05),
                                    0 4px 14px rgba(0,0,0,0.22),
                                    0 0 18px ${alpha(colors.primary.main, 0.08)}
                                `,
                                overflow: "hidden",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "65%",
                                    height: "1px",
                                    background: `
                                        linear-gradient(
                                            90deg,
                                            transparent,
                                            rgba(255,255,255,0.9),
                                            transparent
                                        )
                                    `,
                                    opacity: 0.55,
                                },
                            }}
                        >
                            <LocalFireDepartmentRounded
                                sx={{
                                    fontSize: "0.9rem",
                                    color: colors.primary.main,
                                    filter: `
                                        drop-shadow(0 0 6px ${alpha(colors.primary.main, 0.45)})
                                    `,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: "0.64rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: colors.text.primary,
                                    lineHeight: 1,
                                    fontVariantNumeric: "tabular-nums",
                                    textShadow: `
                                        0 0 10px ${alpha(colors.primary.main, 0.16)}
                                    `,
                                }}
                            >
                                {highestWinStreak}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "0.58rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: alpha(colors.text.secondary, 0.72),
                                    lineHeight: 1,
                                }}
                            >
                                Win Streak
                            </Typography>

                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background: `
                                        linear-gradient(
                                            120deg,
                                            transparent 0%,
                                            rgba(255,255,255,0.035) 48%,
                                            transparent 100%
                                        )
                                    `,
                                    pointerEvents: "none",
                                }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 2, minWidth: 0 }}>
                        <OverviewEloProgressionChart
                            last25MatchData={last25MatchData}
                            isLoading={isLoading}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        ...OVERVIEW_CARD_SX,
                        gridColumn: { lg: 2 },
                        gridRow: { lg: 2 },
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box
                        sx={{
                            ...CARD_HEADER_SX,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.35rem",
                                minWidth: 0,
                            }}
                        >
                            <Typography sx={CARD_TITLE_SX}>Recent Activity</Typography>
                            <Typography sx={CARD_SUBTITLE_SX}>Last 90 days</Typography>
                        </Box>
                        <WinLossRecordLabel
                            wins={recentStats?.wins ?? 0}
                            losses={recentStats?.losses ?? 0}
                        />
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 2, minWidth: 0 }}>
                        <OverviewActivityCalendar
                            matches={activityMatches}
                            isLoading={isLoading}
                        />
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: "1rem",
                    width: "100%",
                    alignItems: "stretch",
                }}
            >
                <Box
                    sx={{
                        ...OVERVIEW_CARD_SX,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box sx={CARD_HEADER_SX}>
                        <Typography sx={CARD_TITLE_SX}>Rating 3.0</Typography>
                        <Typography sx={CARD_SUBTITLE_SX}>Last 20 matches</Typography>
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 2, minWidth: 0 }}>
                        <OverviewRatingTrendChart
                            last25MatchData={last25MatchData}
                            isLoading={isLoading}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        ...OVERVIEW_CARD_SX,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box sx={CARD_HEADER_SX}>
                        <Typography sx={CARD_TITLE_SX}>Map Performance</Typography>
                        <Typography sx={CARD_SUBTITLE_SX}>Best 3 maps</Typography>
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 2, minWidth: 0 }}>
                        <OverviewMapPerformance mapsStats={mapsStats} isLoading={isLoading} />
                    </Box>
                </Box>
            </Box>

        </Box>
    );
}

ProfileOverviewTab.propTypes = {
    last25MatchData: PropTypes.object,
    mapsStats: PropTypes.object,
    activityMatches: PropTypes.arrayOf(
        PropTypes.shape({
            createdAt: PropTypes.number,
        })
    ),
    recentStats: PropTypes.object,
    isLoading: PropTypes.bool,
    onViewAllMatches: PropTypes.func,
    statsBarItems: PropTypes.arrayOf(PropTypes.object),
    statsBarLoading: PropTypes.bool,
};
